import { ExternalReportType } from 'generated/graphql';
import IconEmployeeDonationsByDepartment from 'assets/icons/reports-employee-donations-by-department.svg';
import IconActions from 'assets/icons/reports-actions.svg';
import IconCommunities from 'assets/icons/reports-communities.svg';
import IconDonationsReportComprehensive from 'assets/icons/reports-donations-report-comprehensive.svg';
import IconFundraiserDonationSummary from 'assets/icons/reports-fundraiser-donation-summary.svg';
import IconNonprofitCauseAreasReportDonations from 'assets/icons/reports-nonprofit-cause-areas-report-donations.svg';
import IconNonprofitSdgReportDonations from 'assets/icons/reports-nonprofit-SDG-report-donations.svg';
import IconNonprofitSpotlightReportDonations from 'assets/icons/reports-nonprofit-spotlight-report-donations.svg';
import IconVolunteeringReportComprehensive from 'assets/icons/reports-volunteering-report-comprehensive.svg';
import IconDonationsReportCustom from 'assets/icons/reports-donations-custom.svg';
import IconPledgesReport from 'assets/icons/reports-pledges-icon.svg';
import IconNonVolunteeringReportComprehensive from 'assets/icons/reports-non-volunteering-report-comprehensive.svg';
import IconEngagementReportComprehensive from 'assets/icons/reports-engagement-report-comprehensive.svg';
import IconRatingsReport from 'assets/icons/reports-ratings-report.svg';
import IconAffiliationsReport from 'assets/icons/reports-affiliations-report.svg';
import IconBadgesReport from 'assets/icons/reports-badges.svg';
import IconCampaignsReport from 'assets/icons/reports-campaigns.svg';
import IconUsersReport from 'assets/icons/reports-users-report.svg';

export interface ReportDefinition {
  icon: string;
  family?: string;
  type: ExternalReportType;
  title: string;
  text: string;
}

const allReports = (organizationName: string): ReportDefinition[] => [
  {
    icon: IconDonationsReportCustom,
    type: ExternalReportType.DonationsCustom,
    title: `Custom ${organizationName} Report`,
    text: '',
  },
  {
    icon: IconDonationsReportComprehensive,
    type: ExternalReportType.DonationsComprehensive,
    title: 'Donations Report - Comprehensive',
    text: 'Filter and export Deed’s robust donations report.',
  },
  {
    icon: IconDonationsReportComprehensive,
    type: ExternalReportType.DonationsComprehensiveDataExtract,
    title: 'Data Extract: Donations Report - Comprehensive',
    text: 'Data Extract version of the Donations Report - Comprehensive.',
  },
  {
    icon: IconVolunteeringReportComprehensive,
    type: ExternalReportType.VolunteeringComprehensive,
    title: 'Volunteering Report - Comprehensive',
    text: 'Filter and export Deed’s robust volunteering report.',
  },
  {
    icon: IconVolunteeringReportComprehensive,
    type: ExternalReportType.VolunteeringComprehensiveDataExtract,
    title: 'Data Extract: Volunteering Report - Comprehensive',
    text: 'Data Extract version of the Volunteering Report - Comprehensive.',
  },
  {
    icon: IconNonVolunteeringReportComprehensive,
    type: ExternalReportType.NonVolunteeringComprehensive,
    title: 'Events Report - Comprehensive',
    text: "Filter and export Deed's events report.",
  },
  {
    icon: IconActions,
    type: ExternalReportType.Actions,
    title: 'Actions Report',
    text: "Review and export the line item data for employees' Actions.",
  },
  {
    icon: IconCommunities,
    type: ExternalReportType.Communities,
    title: 'Communities Report',
    text: "Filter and export Deed's Communities report.",
  },
  {
    icon: IconAffiliationsReport,
    type: ExternalReportType.Affiliations,
    title: 'Affiliations Report',
    text: 'See how your employees are affiliated with their favorite nonprofits.',
  },
  {
    icon: IconEmployeeDonationsByDepartment,
    type: ExternalReportType.DonationsByDepartment,
    title: 'Employee Donations by Department',
    text: 'Explore the composition of employee giving, broken out by department.',
  },
  {
    icon: IconFundraiserDonationSummary,
    type: ExternalReportType.FundraiserDonationsSummary,
    title: 'Fundraiser Donations Summary',
    text: 'Monitor the donations results and performance of your Fundraisers.',
  },
  {
    icon: IconNonprofitCauseAreasReportDonations,
    type: ExternalReportType.NonprofitCauseAreas,
    title: 'Nonprofit Cause Areas Report - Donations',
    text: 'Track your donations impact and engagement across cause areas.',
  },
  {
    icon: IconNonprofitSdgReportDonations,
    type: ExternalReportType.NonprofitSdg,
    title: 'Nonprofit SDG Report - Donations',
    text: 'Track your donations and engagement across the UN Sustainable Development Goals.',
  },
  {
    icon: IconNonprofitSpotlightReportDonations,
    type: ExternalReportType.NonprofitSpotlight,
    title: 'Nonprofit Spotlight Report - Donations',
    text: 'Quantify and analyze donations, broken out by nonprofit.',
  },
  {
    icon: IconPledgesReport,
    type: ExternalReportType.PledgesReport,
    title: 'Pledges Report',
    text: "See each of your employees' Pledges.",
  },
  {
    icon: IconPledgesReport,
    type: ExternalReportType.PledgesReportDataExtract,
    title: 'Data Extract: Pledges Report',
    text: 'Data Extract version of the Pledges Report.',
  },
  {
    icon: IconPledgesReport,
    type: ExternalReportType.NpoOnboardingReports,
    title: 'NPO Onboarding Reports',
    text: 'Onboarding status of nonprofit organizers and nonprofits.',
  },
  {
    icon: IconEngagementReportComprehensive,
    type: ExternalReportType.EngagementComprehensive,
    title: 'Engagement Report - Comprehensive',
    text: "Filter and export Deed's robust engagement report.",
  },
  {
    icon: IconRatingsReport,
    type: ExternalReportType.Ratings,
    title: 'Ratings Report',
    text: 'Review how your employees rate deeds after they participate.',
  },
  {
    icon: IconBadgesReport,
    type: ExternalReportType.Badges,
    title: 'Badges Report',
    text: "Filter and export Deed's badges report.",
  },
  {
    icon: IconCampaignsReport,
    type: ExternalReportType.Campaigns,
    title: 'Campaigns Report',
    text: 'See all data related to campaigns in one easy-to-use report.',
  },
  {
    icon: IconUsersReport,
    type: ExternalReportType.UsersReport,
    title: 'Users Report',
    text: 'Key data on all users.',
  },
];

const getReports = (organizationName: string) => allReports(organizationName);

export { ReportDefinition, getReports };
